/* eslint-disable no-console */
import { RELATED_PROJECTS_PARAMS } from './constants'
import { HttpStatus } from '../HttpStatus'
import { getFilteredProjectsPath } from '../api'
import { ErrorResponse, FetchGenericResponse, get } from '../network'
import { createRequestHeaders } from '../cookieHelper'
import { GetServerSidePropsContext } from 'next'
import { Project, ProjectsResponse } from '@/interfaces/projects'
import { logData } from '@/logging/nodeLogger'

const getProjectsFromResponse = (
  projectsResponse: FetchGenericResponse<ProjectsResponse, ErrorResponse>,
  projectIdExt: string,
  queryType: string,
  req?: GetServerSidePropsContext['req']
): Project[] => {
  try {
    if (projectsResponse.status === HttpStatus.OK) {
      return projectsResponse.data?.items?.filter((project) => project.projectIdExt !== projectIdExt) ?? []
    } else {
      logData({
        msg: JSON.stringify({ ...projectsResponse, message: `Error fetching ${queryType} related projects` }),
        severity: 'error',
        clientUrl: req?.url,
        userAgent: req?.headers['user-agent'],
        userId: req?.cookies['uid'],
        service: 'fe-public-projects-ssr'
      })
      return []
    }
  } catch (e: unknown) {
    if (e instanceof Error) {
      logData({
        msg: `Error fetching ${queryType} related projects - ${e.message}`,
        severity: 'error',
        stackTrace: e.stack,
        clientUrl: req?.url,
        userAgent: req?.headers['user-agent'],
        userId: req?.cookies['uid'],
        service: 'fe-public-projects-ssr'
      })
    }
    return []
  }
}

export const getProjectsByCategory = async (
  projectIdExt: string,
  projectCategory: string,
  req: GetServerSidePropsContext['req']
): Promise<Project[]> => {
  const projectsResponse = await get<ProjectsResponse>(
    getFilteredProjectsPath({ ...RELATED_PROJECTS_PARAMS, categoryId: projectCategory }),
    {
      headers: createRequestHeaders(req)
    }
  )
  return getProjectsFromResponse(projectsResponse, projectIdExt, `category ${projectCategory}`, req)
}

export const getProjectsByTags = async (
  projectIdExt: string,
  tags: string[],
  limit: number,
  req: GetServerSidePropsContext['req']
): Promise<Project[]> => {
  let currentTagIndex = 0
  let projects = []

  while (projects.length < limit && currentTagIndex < tags.length) {
    const currentTag = tags[currentTagIndex]

    if (currentTag !== null) {
      const projectsResponse = await get<ProjectsResponse>(
        getFilteredProjectsPath({ ...RELATED_PROJECTS_PARAMS, tag: currentTag }),
        {
          headers: createRequestHeaders(req)
        }
      )
      projects = [...projects, ...getProjectsFromResponse(projectsResponse, projectIdExt, `tag ${currentTag}`)]
    }

    currentTagIndex++
  }

  return projects
}

export const getGeneralProjects = async (projectIdExt, req: GetServerSidePropsContext['req']): Promise<Project[]> => {
  const projectsResponse = await get<ProjectsResponse>(getFilteredProjectsPath(RELATED_PROJECTS_PARAMS), {
    headers: createRequestHeaders(req)
  })
  return getProjectsFromResponse(projectsResponse, projectIdExt, 'general', req)
}
